import { Button } from '@amzn/awsui-components-react';
import React, { useContext, useState } from 'react';
import { isPiezoEnabledEnvironment } from '../../../client/piezo/signing/environment';
import { refreshPiezoMaterial } from '../../../client/piezo/provisioning/refreshPiezoMaterial';
import { isMidwayAuthError, MIDWAY_LOGIN_LABEL } from '../../../client/piezo/signing/midwaySession';
import { useToastNotifications } from '../../../hooks/useToastList';
import { AppContext } from '../../../utils/appContext';

/** Names what signing still uses, since a failed refresh leaves the old material in place. */
const REFRESH_FAILED = 'Piezo refresh failed; signing is still using the previous material';

/**
 * A refresh cannot fix an expired Midway session -- the re-provision it runs is itself Midway
 * authenticated -- so point at the one control that can. Named by the constant the button renders.
 */
const failureMessage = (detail: string): string =>
  isMidwayAuthError(detail)
    ? `${REFRESH_FAILED}: ${detail}. Choose "${MIDWAY_LOGIN_LABEL}" in the navbar.`
    : `${REFRESH_FAILED}: ${detail}`;

/**
 * Re-provisions the operator's Piezo material on demand, for when the cached material is stale rather than
 * missing. Renders only where Piezo runs.
 *
 * Reports through a toast, which requires TumblerHome's ToastList to wrap the navbar -- it used to sit
 * below it, and the empty default context made addDanger throw on the outcome it was reporting.
 */
export const PiezoRefreshButton = () => {
  const appContext = useContext(AppContext);
  const toasts = useToastNotifications();
  const [refreshing, setRefreshing] = useState(false);

  if (!isPiezoEnabledEnvironment()) {
    return null;
  }

  const onClick = async () => {
    setRefreshing(true);
    try {
      const result = await refreshPiezoMaterial(appContext.region.publicRegion);
      if (result.error) {
        toasts.addDanger(failureMessage(result.error.message));
      } else {
        toasts.addSuccess('Piezo material refreshed. Signing now uses the new decks and permits.');
      }
    } catch (err) {
      // refreshPiezoMaterial returns its failure, so reaching here is unexpected.
      toasts.addDanger(failureMessage(String(err)));
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Button
      iconName="refresh"
      variant="normal"
      loading={refreshing}
      // A refresh runs the full cold path, so name it rather than showing a bare spinner.
      loadingText="Refreshing Piezo"
      onClick={onClick}
    >
      Refresh Piezo
    </Button>
  );
};
